import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Copy, EyeOff, ImageOff, Loader2, RefreshCw, Save } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  AppTemplateActionError,
  copyTemplatePreviewLink,
  type AdminAppTemplateCatalogEntry,
  loadAdminAppTemplateCatalog,
  publishAppTemplate,
  saveAdminAppTemplateCatalog,
  unpublishAppTemplate,
  type TemplatePublicationRequest,
  type ReadyForPublicationTemplate,
} from "@/lib/app-templates";

type HomepageSource = "hardcoded" | "catalog";

function ownerLabel(template: AdminAppTemplateCatalogEntry): string {
  const owner = template.owner;
  if (!owner) return "Owner unavailable";
  return owner.name || owner.email || "Unnamed owner";
}

function publishedLabel(value: string | null): string {
  if (!value) return "Publication date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? "Publication date unavailable" : date.toLocaleDateString();
}

function createdLabel(value: string | null): string {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? "Date unavailable" : date.toLocaleDateString();
}

export function TemplateCatalogAdmin() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<AdminAppTemplateCatalogEntry[]>([]);
  const [publicationRequests, setPublicationRequests] = useState<TemplatePublicationRequest[]>([]);
  const [unpublishedTemplates, setUnpublishedTemplates] = useState<TemplatePublicationRequest[]>([]);
  const [readyForPublication, setReadyForPublication] = useState<ReadyForPublicationTemplate[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ id: string; name: string; kind: "publish" | "unpublish" } | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [savedTemplates, setSavedTemplates] = useState<AdminAppTemplateCatalogEntry[]>([]);
  const [homepageSource, setHomepageSource] = useState<HomepageSource>("hardcoded");
  const [savedHomepageSource, setSavedHomepageSource] = useState<HomepageSource>("hardcoded");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const catalog = await loadAdminAppTemplateCatalog();
      setTemplates(catalog.templates);
      setSavedTemplates(catalog.templates);
      setPublicationRequests(catalog.publication_requests);
      setUnpublishedTemplates(catalog.unpublished_templates);
      setReadyForPublication(catalog.ready_for_publication);
      setHomepageSource(catalog.homepage_source);
      setSavedHomepageSource(catalog.homepage_source);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const dirty = useMemo(() => (
    homepageSource !== savedHomepageSource
    || templates.some((template, index) => (
      template.id !== savedTemplates[index]?.id
      || template.is_visible !== savedTemplates[index]?.is_visible
    ))
    || templates.length !== savedTemplates.length
  ), [homepageSource, savedHomepageSource, savedTemplates, templates]);
  const visibleCount = templates.filter((template) => template.is_visible).length;

  const move = (index: number, direction: -1 | 1) => {
    const destination = index + direction;
    if (destination < 0 || destination >= templates.length) return;
    setTemplates((current) => {
      const next = [...current];
      [next[index], next[destination]] = [next[destination], next[index]];
      return next;
    });
  };

  const copyTemplateLink = async (template: AdminAppTemplateCatalogEntry) => {
    try {
      await copyTemplatePreviewLink(template.id);
      toast({ title: "Template link copied", description: `Share ${template.name} from the Template catalog.` });
    } catch {
      toast({ title: "Could not copy link", description: "Copy the link from your browser address bar instead.", variant: "destructive" });
    }
  };

  const confirmPublication = async () => {
    if (!confirmTarget || publishingId || saving || dirty) return;
    const { id, kind } = confirmTarget;
    setPublishingId(id);
    try {
      if (kind === "publish") {
        await publishAppTemplate(id);
      } else {
        await unpublishAppTemplate(id);
      }
      setConfirmTarget(null);
      toast({
        title: kind === "publish" ? "Template published" : "Template unpublished",
        description: kind === "publish"
          ? "The Template is available in the public catalog."
          : "The public link no longer permits new copies. Existing Apps are unaffected.",
      });
      await load();
    } catch (error) {
      toast({
        title: kind === "publish" ? "Could not publish Template" : "Could not unpublish Template",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setPublishingId(null);
    }
  };

  const save = async () => {
    if (saving || publishingId || !dirty) return;
    setSaving(true);
    try {
      await saveAdminAppTemplateCatalog({
        expectedTemplateIds: savedTemplates.map((template) => template.id),
        templates,
        homepageSource,
      });
      setSavedTemplates(templates);
      setSavedHomepageSource(homepageSource);
      toast({ title: "Template catalog saved" });
    } catch (error) {
      const stale = error instanceof AppTemplateActionError && error.code === "catalog_stale";
      toast({
        title: stale ? "Templates changed" : "Catalog not saved",
        description: stale ? "Refresh to include the latest published Templates before saving." : "Try again or refresh the catalog.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CatalogLoadingState />;
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-3 p-6">
          <p className="font-medium">The Template catalog could not be loaded.</p>
          <Button variant="outline" onClick={() => void load()}><RefreshCw className="mr-2 h-4 w-4" />Try again</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <section className="space-y-6" aria-labelledby="template-catalog-heading">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 id="template-catalog-heading" className="text-xl font-semibold text-foreground">Templates</h2>
          <p className="mt-1 text-sm text-muted-foreground">Review owner submissions and manage public access separately from catalog visibility.</p>
        </div>
        <Button onClick={() => void save()} disabled={!dirty || saving || publishingId !== null}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save changes
        </Button>
      </div>

      <Card>
        <CardContent className="space-y-4 p-5">
          <div>
            <h3 className="font-medium">Homepage gallery</h3>
            <p className="mt-1 text-sm text-muted-foreground">Choose the source shown in the homepage gallery.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Homepage gallery source">
            {(["hardcoded", "catalog"] as const).map((source) => (
              <label key={source} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-4 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input
                  type="radio"
                  name="homepage-source"
                  value={source}
                  checked={homepageSource === source}
                  onChange={() => setHomepageSource(source)}
                  className="mt-1"
                />
                <span>
                  <span className="block font-medium">{source === "hardcoded" ? "Hardcoded screenshots" : "Catalog Templates"}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    {source === "hardcoded" ? "Keep the current homepage gallery." : "Show the first six visible Templates."}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {homepageSource === "catalog" && visibleCount < 6 && (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="status">
              <EyeOff className="mt-0.5 h-4 w-4 shrink-0" />
              Catalog mode has {visibleCount} visible {visibleCount === 1 ? "Template" : "Templates"}. The homepage will show the available set; you can still save this choice.
            </div>
          )}
        </CardContent>
      </Card>

      {dirty && <p role="status" className="text-sm text-muted-foreground">Save your catalog visibility changes before publishing or unpublishing.</p>}
      <section aria-labelledby="template-ready-heading" className="space-y-3">
        <h3 id="template-ready-heading" className="text-lg font-semibold">Ready for Publication ({readyForPublication.length})</h3>
        <p className="text-sm text-muted-foreground">Review a completed private snapshot before making its captured structure and data public.</p>
        {readyForPublication.length === 0 ? (
          <Card><CardContent className="p-5 text-sm text-muted-foreground">No Templates are ready for publication.</CardContent></Card>
        ) : readyForPublication.map((template) => (
          <Card key={template.id}>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="font-medium">{template.name}</p>
                <p className="text-sm text-muted-foreground">{template.owner} · Created {createdLabel(template.created_at)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" asChild><Link to={`/applications/${template.id}`}>Review Template</Link></Button>
                <Button disabled={dirty || saving || publishingId !== null}
                  onClick={() => setConfirmTarget({ id: template.id, name: template.name, kind: "publish" })}>
                  Publish Template
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>
      <section aria-labelledby="template-requests-heading" className="space-y-3">
        <h3 id="template-requests-heading" className="text-lg font-semibold">Publication requests ({publicationRequests.length})</h3>
        <p className="text-sm text-muted-foreground">Review the snapshot before publishing. Publication allows others to copy its captured structure and data.</p>
        {publicationRequests.length === 0 ? (
          <Card><CardContent className="p-5 text-sm text-muted-foreground">No Templates are waiting for review.</CardContent></Card>
        ) : publicationRequests.map((request) => (
          <Card key={request.id}>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="font-medium">{request.name}</p>
                <p className="text-sm text-muted-foreground">{request.owner} · Requested {publishedLabel(request.requested_at)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" asChild><Link to={`/applications/${request.id}`}>Review Template</Link></Button>
                <Label htmlFor={`publish-request-${request.id}`}>Published</Label>
                <Switch id={`publish-request-${request.id}`} checked={false} disabled={dirty || saving || publishingId !== null}
                  onCheckedChange={(checked) => { if (checked) setConfirmTarget({ id: request.id, name: request.name, kind: "publish" }); }}
                  aria-label={`Publish ${request.name}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      {unpublishedTemplates.length > 0 && (
        <section aria-labelledby="template-unpublished-heading" className="space-y-3">
          <h3 id="template-unpublished-heading" className="text-lg font-semibold">Unpublished Templates ({unpublishedTemplates.length})</h3>
          <p className="text-sm text-muted-foreground">These snapshots are private again. Turn Published on to restore public access.</p>
          {unpublishedTemplates.map((template) => (
            <Card key={template.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="font-medium">{template.name}</p>
                  <p className="text-sm text-muted-foreground">{template.owner}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button asChild size="sm" variant="outline"><Link to={`/applications/${template.id}`}>Review Template</Link></Button>
                  <Label htmlFor={`republish-${template.id}`}>Published</Label>
                  <Switch id={`republish-${template.id}`} checked={false} disabled={dirty || saving || publishingId !== null}
                    onCheckedChange={(checked) => { if (checked) setConfirmTarget({ id: template.id, name: template.name, kind: "publish" }); }}
                    aria-label={`Republish ${template.name}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      <div className="space-y-1">
        <h3 className="text-lg font-semibold">Published Templates</h3>
        <p className="text-sm text-muted-foreground">The Visible switch controls catalog listing, not publication. Unpublish revokes public access.</p>
      </div>
      {templates.length === 0 ? (
        <Card><CardContent className="p-6 text-sm text-muted-foreground">There are no active Templates to manage.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {templates.map((template, index) => (
            <TemplateRow
              key={template.id}
              template={template}
              index={index}
              total={templates.length}
              onCopyLink={() => void copyTemplateLink(template)}
              onVisibilityChange={(is_visible) => setTemplates((current) => current.map((item) => item.id === template.id ? { ...item, is_visible } : item))}
              onMove={move}
              onUnpublish={template.is_snapshot ? () => setConfirmTarget({ id: template.id, name: template.name, kind: "unpublish" }) : undefined}
              actionDisabled={dirty || saving || publishingId !== null}
            />
          ))}
        </div>
      )}
      <AlertDialog open={confirmTarget !== null} onOpenChange={(open) => { if (!open && !publishingId) setConfirmTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTarget?.kind === "publish" ? "Publish this Template?" : "Unpublish this Template?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget?.kind === "publish"
                ? `"${confirmTarget?.name}" contains an immutable copy of the owner's App structure and data. Publishing will allow people to preview and create independent copies.`
                : `"${confirmTarget?.name}" will be removed from public discovery and new public copies will be blocked immediately. Existing customer Apps will not change. You can publish it again later.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishingId !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={dirty || saving || publishingId !== null} onClick={(event) => { event.preventDefault(); void confirmPublication(); }}>
              {publishingId ? "Saving…" : confirmTarget?.kind === "publish" ? "Publish Template" : "Unpublish Template"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function TemplateRow({ template, index, total, onVisibilityChange, onMove, onCopyLink, onUnpublish, actionDisabled }: {
  template: AdminAppTemplateCatalogEntry;
  index: number;
  total: number;
  onVisibilityChange: (value: boolean) => void;
  onMove: (index: number, direction: -1 | 1) => void;
  onCopyLink: () => void;
  onUnpublish?: () => void;
  actionDisabled: boolean;
}) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="grid gap-4 p-4 sm:grid-cols-[104px_minmax(0,1fr)_auto] sm:items-center">
        <div className="aspect-[4/3] overflow-hidden rounded-md border bg-muted">
          {template.preview_image_url ? (
            <img src={template.preview_image_url} alt={`${template.name} preview`} className="h-full w-full object-cover object-top" />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground"><ImageOff className="h-5 w-5" /><span className="sr-only">Preview image placeholder</span></div>
          )}
        </div>
        <div className="min-w-0">
          <h3 className="truncate font-medium">{template.name}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{ownerLabel(template)} · {template.owner?.role ?? "Unknown role"}</p>
          <p className="mt-1 text-xs text-muted-foreground">Published {publishedLabel(template.published_at)}</p>
        </div>
        <div className="flex items-center justify-between gap-3 border-t pt-3 sm:border-l sm:border-t-0 sm:pt-0 sm:pl-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onCopyLink}>
              <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              Copy link
            </Button>
            <Label htmlFor={`visibility-${template.id}`} className="text-sm">Visible</Label>
            <Switch
              id={`visibility-${template.id}`}
              checked={template.is_visible}
              onCheckedChange={onVisibilityChange}
              aria-label={`Show ${template.name} in discovery`}
            />
            {onUnpublish && <>
              <Label htmlFor={`published-${template.id}`} className="text-sm">Published</Label>
              <Switch id={`published-${template.id}`} checked={true} disabled={actionDisabled}
                onCheckedChange={(checked) => { if (!checked) onUnpublish?.(); }}
                aria-label={`Unpublish ${template.name}`} />
            </>}
          </div>
          <div className="flex items-center">
            <Button variant="ghost" size="icon" onClick={() => onMove(index, -1)} disabled={index === 0} aria-label={`Move ${template.name} up`}><ChevronUp className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => onMove(index, 1)} disabled={index === total - 1} aria-label={`Move ${template.name} down`}><ChevronDown className="h-4 w-4" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CatalogLoadingState() {
  return (
    <div className="space-y-3" aria-label="Loading Template catalog">
      {[0, 1, 2].map((item) => <div key={item} className="h-28 animate-pulse rounded-lg border bg-muted/40" />)}
    </div>
  );
}
